import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ThumbsUp, ThumbsDown, PenLine, Loader2 } from 'lucide-react';
import type { PublicBusinessContactInfo } from '@/lib/api';
import { getBusinessInitials, getPresetIconComponent } from '@/components/TemplateDesigner/utils';

interface FullDocumentViewProps {
  documentType: 'invoice' | 'quote' | 'purchase_order' | 'credit_memo';
  business: PublicBusinessContactInfo;
  status: string;
  statusLabel: string;
  statusVariant?: 'default' | 'success' | 'warning' | 'destructive';
  // For quotes with approve/decline actions
  showApproveDecline?: boolean;
  onApprove?: () => void;
  onDecline?: () => void;
  approving?: boolean;
  declining?: boolean;
  // For signature flow
  showSignButton?: boolean;
  signButtonText?: string;
  onSign?: () => void;
  signing?: boolean;
  // PDF download - either a URL or a callback for on-demand generation
  pdfUrl?: string | null;
  onDownloadPdf?: () => Promise<Blob>;
  pdfFilename?: string;
  // Template logo settings
  themeColor?: string;
  logoUrl?: string | null; // Template logo.url - uploaded image, icon:*, or null
  showCompanyName?: boolean; // Template logo.showCompanyName
  // The rendered document content
  children: ReactNode;
}

export function FullDocumentView({
  documentType,
  business,
  status,
  statusLabel,
  statusVariant = 'default',
  showApproveDecline = false,
  onApprove,
  onDecline,
  approving = false,
  declining = false,
  showSignButton = false,
  signButtonText,
  onSign,
  signing = false,
  pdfUrl,
  onDownloadPdf,
  pdfFilename,
  themeColor = '#6366f1',
  logoUrl,
  showCompanyName = true,
  children,
}: FullDocumentViewProps) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (!onDownloadPdf) return;

    setDownloadingPdf(true);
    try {
      const blob = await onDownloadPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };
  const getStatusBadgeClass = () => {
    switch (statusVariant) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'destructive':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getDocumentTypeLabel = () => {
    switch (documentType) {
      case 'invoice':
        return 'Invoice';
      case 'quote':
        return 'Estimate';
      case 'purchase_order':
        return 'Purchase Order';
      case 'credit_memo':
        return 'Credit Memo';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Render logo/icon based on template settings */}
            {(() => {
              const initials = getBusinessInitials(business.name);
              const initialsFontSize = initials.length === 1 ? '1.25rem' : '0.875rem';

              // Determine what to show for logo/icon (only if explicitly set)
              let logoElement: React.ReactNode = null;

              if (logoUrl?.startsWith('icon:')) {
                // Preset icon from template
                const iconId = logoUrl.replace('icon:', '');
                if (iconId === 'initials') {
                  // Initials icon
                  logoElement = (
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold shrink-0"
                      style={{ backgroundColor: themeColor, fontSize: initialsFontSize }}
                    >
                      {initials}
                    </div>
                  );
                } else {
                  // Other preset icons (briefcase, hammer, etc.)
                  const IconComponent = getPresetIconComponent(iconId);
                  if (IconComponent) {
                    logoElement = (
                      <div
                        className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0"
                        style={{ backgroundColor: themeColor }}
                      >
                        <IconComponent className="h-6 w-6" />
                      </div>
                    );
                  }
                }
              } else if (logoUrl) {
                // Uploaded image (data URL or regular URL)
                logoElement = (
                  <img
                    src={logoUrl}
                    alt={business.name}
                    className="h-10 w-auto object-contain"
                  />
                );
              }
              // Note: no fallback to initials here - only show if explicitly configured

              // If nothing is configured (no logo AND company name disabled), show company name as fallback
              const hasLogo = logoElement !== null;
              const shouldShowName = showCompanyName || (!hasLogo && !showCompanyName);

              return (
                <div className="flex items-center gap-3">
                  {logoElement}
                  {shouldShowName && (
                    <span className="font-semibold text-gray-900 text-lg">
                      {business.name}
                    </span>
                  )}
                </div>
              );
            })()}
            <div className="h-8 w-px bg-gray-200" />
            <Badge variant="outline" className={getStatusBadgeClass()}>
              {statusLabel}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {/* Sign button for signature flow (with optional decline) */}
            {showSignButton && onSign && (
              <>
                {onDecline && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onDecline}
                    disabled={declining || signing}
                  >
                    {declining ? (
                      'Declining...'
                    ) : (
                      <>
                        <ThumbsDown className="h-4 w-4 mr-2" />
                        Request Changes
                      </>
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={onSign}
                  disabled={signing || declining}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {signing ? (
                    'Signing...'
                  ) : (
                    <>
                      <PenLine className="h-4 w-4 mr-2" />
                      {signButtonText || 'Sign'}
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Approve/Decline buttons for quotes (without signature requirement) */}
            {showApproveDecline && onApprove && onDecline && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDecline}
                  disabled={declining || approving}
                >
                  {declining ? (
                    'Declining...'
                  ) : (
                    <>
                      <ThumbsDown className="h-4 w-4 mr-2" />
                      Request Changes
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={onApprove}
                  disabled={approving || declining}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {approving ? (
                    'Approving...'
                  ) : (
                    <>
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      Approve
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Download PDF - prefer on-demand generation to ensure signatures are current */}
            {onDownloadPdf ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {downloadingPdf ? 'Generating...' : 'Download PDF'}
              </Button>
            ) : pdfUrl ? (
              <Button variant="outline" size="sm" asChild>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </a>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Document content */}
      <main className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex justify-center">
          {/* 816px = 8.5 inches at 96 DPI (US Letter width) */}
          <div className="w-full max-w-[816px] bg-white rounded-lg shadow-sm overflow-hidden">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-8">
        <div className="max-w-5xl mx-auto px-4 py-6 text-center text-sm text-gray-500">
          <p>{getDocumentTypeLabel()} from {business.name}</p>
          {business.email && (
            <p className="mt-1">
              Questions? Contact us at{' '}
              <a href={`mailto:${business.email}`} className="text-primary hover:underline">
                {business.email}
              </a>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
